import { AfterViewInit, Component, computed, ElementRef, inject, signal, viewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../core/utils";
import { Constants } from "../../constants/constants";
import { Language } from "../../constants/enums";
import { LanguageService, ValidatorApiService } from "../../services";
import { ConfirmationModalComponent } from "../general";

@Component({
  selector: "adaa-system-tour",
  imports: [TranslatePipe],
  styleUrl: "./system-tour.scss",
  template: ` @if (visible()) {
    <div class="tour-overlay"></div>
    <div
      #tooltip
      class="tour-tooltip"
      [style]="position()"
      [style.direction]="isArabic() ? 'rtl' : 'ltr'"
      [class.arrow-up]="arrowDirection() === 'up'"
      [class.arrow-down]="arrowDirection() === 'down'"
      [class.arrow-left]="arrowDirection() === 'left'"
      [class.arrow-right]="arrowDirection() === 'right'"
    >
      <p>{{ text() | translate }}</p>
      <button class="btn btn-primary mx-5" (click)="skip()">
        {{ isLastStep ? ("common.form.button.finish" | translate) : ("common.form.button.skip" | translate) }}
      </button>
      <button class="btn btn-primary mx-1" [disabled]="isFirstStep" (click)="prev()"><</button>
      <button class="btn btn-primary mx-1" [disabled]="isLastStep" (click)="next()">></button>
    </div>
  }`,
})
export class SystemTourComponent implements AfterViewInit {
  private readonly _modalService = inject(NgbModal);
  private readonly _languageService = inject(LanguageService);
  private readonly _validatorApiService = inject(ValidatorApiService);

  private _currentStep = 0;
  private _steps: Array<{ selector: string; text: string; position: string }> = [];

  visible = signal<boolean>(false);
  text = signal<string>("");
  position = signal<{ top: string; left: string }>({ top: "0px", left: "0px" });
  arrowDirection = signal<"up" | "down" | "right" | "left">("down");

  tooltip = viewChild.required<ElementRef>("tooltip");

  readonly isArabic = computed(() => AdaaHelper.getCurrentLang() === Language.Arabic);

  public get isLastStep(): boolean {
    return this._currentStep >= this._steps.length - 1;
  }

  public get isFirstStep(): boolean {
    return this._currentStep <= 0;
  }

  public ngAfterViewInit(): void {
    const tourShowedBefore = AdaaHelper.getLocalStorage(Constants.localStorageKeys.systemTour) ?? "false";
    if (tourShowedBefore === "false" && !AdaaHelper.isMobile()) this._showModal();
  }

  public next(): void {
    if (this._currentStep < this._steps.length - 1) {
      this._currentStep++;
      this._showStep();
    }
  }

  public prev(): void {
    if (this._currentStep > 0) {
      this._currentStep--;
      this._showStep();
    }
  }

  public skip(): void {
    this.visible.set(false);
    this._currentStep = 0;
    this._removeHighlight();
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.systemTour, true, "string");
  }

  private _showModal(): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "guided_tour.modal_title";
    modal.componentInstance.title = "guided_tour.question";

    modal.result.then((e) => {
      if (e) {
        this._getTourSteps();
      } else {
        AdaaHelper.setLocalStorage(Constants.localStorageKeys.systemTour, true, "string");
      }
    });
  }

  private _showStep(): void {
    this._removeHighlight();

    const step = this._steps[this._currentStep];
    if (!step) return;

    const el = this._findVisibleElement(step.selector);
    if (!el) return;

    this._highlightElement(el);
    const rect = el.getBoundingClientRect();
    const spacing = 10;

    this.text.set(step.text);

    // Wait for tooltip to render with updated text
    setTimeout(() => {
      const tooltipEl = this.tooltip().nativeElement as HTMLElement;
      if (!tooltipEl) return;

      const tooltipRect = tooltipEl.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      let top: number;
      let left: number;

      switch (step.position) {
        case "right":
          top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
          left = rect.left + window.scrollX + rect.width + spacing;
          this.arrowDirection.set("left");
          break;

        case "left":
          top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
          left = rect.left + window.scrollX - spacing - tooltipWidth;
          this.arrowDirection.set("right");
          break;

        case "top":
          top = rect.top + window.scrollY - spacing - tooltipHeight;
          left = rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2;
          this.arrowDirection.set("down");
          break;

        case "bottom":
        default:
          top = rect.bottom + window.scrollY + spacing;
          left = rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2;
          this.arrowDirection.set("up");
          break;
      }

      this.position.set({
        top: `${top}px`,
        left: `${left}px`,
      });

      el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  private _findVisibleElement(selector: string): HTMLElement | undefined {
    return Array.from(document.querySelectorAll<HTMLElement>(selector)).find((el) => el.getClientRects().length > 0);
  }

  private _highlightElement(el: HTMLElement): void {
    el.classList.add("tour-highlighted-element");
  }

  private _removeHighlight(): void {
    document
      .querySelectorAll(".tour-highlighted-element")
      .forEach((el) => el.classList.remove("tour-highlighted-element"));
  }

  private _getTourSteps(): void {
    this._validatorApiService.searchByKey(Constants.SYSTEM_TOUR_KEY.DASHBOARD_TARGET_GUIDED_TOUR).subscribe({
      next: (response) => {
        if (response.inError) return;
        response.responseData.parameterCatalogs.sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1));
        this._steps = response.responseData.parameterCatalogs.map((step) => ({
          selector: step.fieldName,
          text: AdaaHelper.getItemValueByToken(step, "description"),
          position: AdaaHelper.getItemValueByToken(step, "position") ?? "bottom",
        }));
      },
      complete: () => {
        this.visible.set(true);
        this._showStep();
      },
    });
  }
}
